import { createContext, useContext, useMemo, useState, type PropsWithChildren } from 'react'
import { useTranslation } from 'react-i18next'

import { isTechnicalPerfilamientoError } from '@/features/perfilamiento/services/perfilamiento.service'
import { Button } from '@/shared/components/ui/Button'
import { WarningTriangleIcon } from '@/shared/components/ui/icons/WarningTriangleIcon'

type PerfilamientoErrorContextValue = {
  showError: (message?: string | null, errorCode?: string | null) => void
  clearError: () => void
}

const PerfilamientoErrorContext = createContext<PerfilamientoErrorContextValue | null>(null)

export function PerfilamientoErrorProvider({ children }: PropsWithChildren) {
  const [error, setError] = useState<string | null>(null)

  const value = useMemo(
    () => ({
      showError: (message?: string | null, errorCode?: string | null) => {
        setError(isTechnicalPerfilamientoError(errorCode ?? 'INTERNAL_ERROR') ? message ?? null : null)
      },
      clearError: () => setError(null),
    }),
    [],
  )

  const { t } = useTranslation('perfilamiento')

  return (
    <PerfilamientoErrorContext.Provider value={value}>
      {children}

      {error ? (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-[rgba(5,20,31,0.45)] px-4">
          <div className="w-full max-w-[420px] rounded-[12px] bg-[#EEF7FC] px-8 py-8 text-center shadow-[0_20px_60px_rgba(5,20,31,0.18)]">
            <div className="mx-auto flex h-9 w-9 scale-[2.25] items-center justify-center">
              <WarningTriangleIcon />
            </div>

            <h2 className="mt-5 text-[26px] font-semibold leading-6 text-[var(--color-text-primary)]">
              {t('questionnaire.errorModal.title')}
            </h2>

            <p className="mt-4 whitespace-pre-line text-[16px] leading-6 text-black">
              {t('questionnaire.errorModal.description')}
            </p>

            <Button className="mt-12 !bg-[#FB3748]" onClick={() => window.location.reload()}>
              {t('questionnaire.errorModal.accept')}
            </Button>
          </div>
        </div>
      ) : null}
    </PerfilamientoErrorContext.Provider>
  )
}

export function usePerfilamientoError() {
  const context = useContext(PerfilamientoErrorContext)

  if (!context) {
    throw new Error('usePerfilamientoError must be used within PerfilamientoErrorProvider')
  }

  return context
}
